import {
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useRef, useState } from "react";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { useDebounce } from "@/hooks/use-debounce";
import { useGiftStore } from "@/components/context/gift-store";
import { decode } from "html-entities";

const fetchSearchResults = async (
  term: string | undefined,
  lang: string,
  categoryID?: string,
  appToken?: string,
) => {
  const params = new URLSearchParams({
    term: term || "",
    lang,
  });

  if (categoryID) params.append("category_id", categoryID);

  const res = await fetch(`/api/search-results?${params.toString()}`, {
    headers: appToken ? { Authorization: appToken } : undefined,
  });

  if (!res.ok) throw new Error("Failed to fetch search results");

  return res.json();
};

interface ComponentProps {
  showWarning: boolean;
  outsideRef: any;
  lang: string;
}

const CustomSearchInput: React.FC<ComponentProps> = ({ showWarning, outsideRef, lang }) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { i18n } = useLingui();
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const queryClient = useQueryClient();
  const selectedcategoryID = useRef("");
  const inputRef = useRef<HTMLDivElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const { setProductId, setErrorMessage, errors } = useGiftStore();
  const closeOpenDropdown = (e: any) => {
    if (showDropdown && !searchDropdownRef.current?.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  const handleBoxClick = () => {
    if (outsideRef.current) {
      const boxTop = outsideRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetTop = boxTop - 125;
      window.scrollTo({ top: offsetTop, behavior: "smooth" });
    }
  };

  const handleInputChange = (event: any) => {
    setSearchTerm(event.target.value);
    selectedcategoryID.current = "";
    if (event.target.value.length >= 3) {
      setShowDropdown(true);
    }
  };

  //selecting a product from the dropdown
  const selectProduct = (id: string) => {
    const selectItem = data?.data?.products.find((item: any) => item.product_id == id);
    setProductId(selectItem.product_id);
    queryClient.invalidateQueries({ queryKey: ["product"] });
    setShowDropdown(false);
    setSearchTerm("");
    setErrorMessage("product", "");
  };

  const setCategoryResults = (id: string) => {
    selectedcategoryID.current = id;
    queryClient.invalidateQueries({ queryKey: ["search"] });
  };
  const [dropdownProducts, setDropdownProducts] = useState<any[]>([]);
  const [dropdownCategories, setDropdownCategories] = useState<any[]>([]);
  //fetch products
  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  const { data, isPending } = useQuery({
    queryKey: ["search", debouncedSearchTerm],
    queryFn: () => fetchSearchResults(debouncedSearchTerm, lang, selectedcategoryID.current),
    enabled: debouncedSearchTerm.length >= 3,
  });

  const renderDropdownItems = (products: any[], categories: any[]) => {
    setDropdownProducts(products);
    setDropdownCategories(categories);
  };

  // used to fill dropdown with items
  useEffect(() => {
    if (data?.success) {
      renderDropdownItems(data?.data?.products, data?.data?.categories);
    }
  }, [data]);

  // used to draw dropdown
  function drawDropdown() {
    if (inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect(); // Get the input's position

      setDropdownPosition({
        top: inputRect.bottom + window.scrollY, // Position it right below the input
        left: inputRect.left + window.scrollX, // Align with input left
        width: inputRect.width, // Match input width
      });
    }
  }

  // reset dropdown
  useEffect(() => {
    if (inputRef.current && showDropdown) {
      window.removeEventListener("resize", drawDropdown);
      window.removeEventListener("mousedown", closeOpenDropdown);
      window.addEventListener("mousedown", closeOpenDropdown);

      drawDropdown();
      window.addEventListener("resize", () => {
        if (showDropdown) {
          drawDropdown();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDropdown]);

  return (
    <Box
      ref={outsideRef}
      onClick={handleBoxClick}
      sx={{
        width: "100%",
        marginTop: "20px",
      }}
    >
      <TextField
        data-testid="search-input"
        placeholder={t(i18n)`Like PlayStation, Netflix etc...`}
        onChange={handleInputChange}
        value={searchTerm}
        autoComplete="off"
        variant="outlined"
        ref={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            fontSize: "17px",
            lineHeight: "1.35",
            color: "#1f1f25",
            padding: "12px 15px 12px",
            width: "100%",
            border: "2px solid #bcbfc1",
            ".MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
          },
        }}
      />
      {showWarning && errors && errors.find((element) => element.key == "product") && (
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#fd7b7c", pt: "4px" }}>
          {errors.find((element) => element.key == "product")?.error}
        </Typography>
      )}
      {showDropdown && (
        <Box
          ref={searchDropdownRef}
          sx={{
            position: "absolute",
            top: `${dropdownPosition?.top}px`, // Use calculated top position
            left: `${dropdownPosition?.left}px`, // Use calculated left position
            width: `${dropdownPosition?.width}px`, // Match input width
            border: "1px solid #ccc",
            overflowY: "auto",
            backgroundColor: "white",
            margin: "0",
            listStyleType: "none",
            padding: "15px",
            maxHeight: "350px",
            zIndex: 10,
          }}
        >
          {isPending && (
            <Box className="flex min-h-[200px] items-center justify-center">
              <Image src="/loader.gif" width="30" height="30" alt="Loader" loading="lazy" />
            </Box>
          )}
          {!isPending && dropdownProducts?.length === 0 && (
            <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
              <Trans>No products available</Trans>
            </Typography>
          )}

          {!isPending && dropdownProducts?.length > 0 && (
            <>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, marginBottom: "10px" }}>
                <Trans>Categories</Trans>
              </Typography>
              <List>
                {Object.keys(dropdownCategories).map((key: any) => (
                  <ListItem key={key} disablePadding>
                    <ListItemButton
                      onClick={() => setCategoryResults(key)}
                      component="button"
                      sx={{
                        color: "#0097ab",
                        fontSize: "17px",
                        lineHeight: 1.35,
                        padding: "0px",
                      }}
                    >
                      <ListItemText primary={decode(dropdownCategories[key])} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "10px",
                  marginTop: "10px",
                }}
              >
                <Trans>Products</Trans>
              </Typography>
              <List>
                {dropdownProducts.map((productItem: any) => (
                  <ListItem key={productItem.id} disablePadding>
                    <ListItemButton
                      onClick={() => selectProduct(productItem.product_id)}
                      component="button"
                      sx={{
                        padding: "0px",
                        my: "5px",
                      }}
                    >
                      <Image
                        loading="lazy"
                        src={productItem.thumb}
                        alt=""
                        width={90}
                        height={60}
                        className="self-center"
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "16px",
                            display: "-webkit-flex",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            WebkitLineClamp: "2",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {decode(productItem.name)}
                        </Typography>
                        {!productItem.special ? (
                          <Typography variant="button" sx={{ fontSize: "14px" }}>
                            {productItem.price}
                          </Typography>
                        ) : (
                          <Typography variant="button" sx={{ color: "red", fontSize: "14px" }}>
                            {productItem.special}
                            <Typography
                              variant="button"
                              sx={{
                                textDecoration: "line-through",
                                color: "red",
                                fontSize: "12px",
                                marginLeft: "5px",
                              }}
                            >
                              {productItem.price}
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
export default CustomSearchInput;
